import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../client";

const BASE_URL = "identity/Fixer";

export const getFixers = createAsyncThunk("Fixer/list", async () =>
  (await api.get(BASE_URL)).data
);

export const getFixer = createAsyncThunk("Fixer", async (id) =>
  (await api.get(`${BASE_URL}/${id}`)).data
);

export const updateFixerStatus = createAsyncThunk(
  "Fixer/updateStatus",
  async ({ id, status, comment }) =>
    (await api.patch(`${BASE_URL}/${id}/status`, { status, comment })).data,
);

export const getFixerDocuments = createAsyncThunk(
  "Fixer/documents",
  async (userId) => (await api.get(`shared/Documents/${userId}`)).data,
);

export const getFixerAddresses = createAsyncThunk(
  "Fixer/addresses",
  async (userId) => (await api.get(`shared/Address/${userId}`)).data,
);

export const verifyFixerDocument = createAsyncThunk(
  "Fixer/verifyDocument",
  async ({ userId, documentType, status, comment }) =>
    (await api.patch(`shared/documents/${userId}/${documentType}/verify`, { status, comment })).data,
);

export const getFixerPayments = createAsyncThunk(
  "Fixer/payments",
  async (fixerId) => (await api.get(`identity/payments/Fixer/${fixerId}`)).data,
);

const addCases = (builder, thunk, onFulfilled, clearMessage = false) =>
  builder
    .addCase(thunk.pending, (state) => {
      state.isLoading = true;
      if (clearMessage) state.message = "";
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state.isLoading = false;
      onFulfilled(state, action);
    })
    .addCase(thunk.rejected, (state, action) => {
      state.isLoading = false;
      state.message = action.payload;
    });

const initialState = {
  list: [],
  fixer: null,
  documents: [],
  addresses: [],
  payments: [],
  paymentsTotalCollected: 0,
  isLoading: false,
  isUpdatingStatus: false,
  isLoadingDocuments: false,
  isLoadingAddresses: false,
  isLoadingPayments: false,
  verifyingDocumentType: null,
  message: null,
};

const slice = createSlice({
  name: "fixers",
  initialState,
  reducers: {
    clearFixer: (state) => {
      state.fixer = null;
      state.documents = [];
      state.addresses = [];
      state.payments = [];
      state.paymentsTotalCollected = 0;
    },
  },
  extraReducers: (builder) => {
    addCases(builder, getFixers, (state, action) => {
      state.list = action.payload.data;
    });
    addCases(builder, getFixer, (state, action) => {
      state.fixer = action.payload.data;
    });

    builder
      .addCase(getFixerDocuments.pending, (state) => {
        state.isLoadingDocuments = true;
      })
      .addCase(getFixerDocuments.fulfilled, (state, action) => {
        state.isLoadingDocuments = false;
        state.documents = action.payload.data ?? [];
      })
      .addCase(getFixerDocuments.rejected, (state, action) => {
        state.isLoadingDocuments = false;
        state.message = action.payload;
      });

    builder
      .addCase(getFixerAddresses.pending, (state) => {
        state.isLoadingAddresses = true;
      })
      .addCase(getFixerAddresses.fulfilled, (state, action) => {
        state.isLoadingAddresses = false;
        state.addresses = action.payload.data ?? [];
      })
      .addCase(getFixerAddresses.rejected, (state, action) => {
        state.isLoadingAddresses = false;
        state.message = action.payload;
      });

    builder
      .addCase(getFixerPayments.pending, (state) => {
        state.isLoadingPayments = true;
      })
      .addCase(getFixerPayments.fulfilled, (state, action) => {
        state.isLoadingPayments = false;
        state.payments = action.payload.data?.items ?? [];
        state.paymentsTotalCollected = action.payload.data?.totalCollected ?? 0;
      })
      .addCase(getFixerPayments.rejected, (state, action) => {
        state.isLoadingPayments = false;
        state.message = action.payload;
      });

    builder
      .addCase(verifyFixerDocument.pending, (state, action) => {
        state.verifyingDocumentType = action.meta.arg.documentType;
        state.message = "";
      })
      .addCase(verifyFixerDocument.fulfilled, (state, action) => {
        state.verifyingDocumentType = null;
        state.message = action.payload.message;
        const { documentType, status, comment } = action.meta.arg;
        const doc = state.documents.find(
          (d) => d.documentType?.toLowerCase() === documentType.toLowerCase(),
        );
        if (doc) {
          doc.verificationStatus = status;
          doc.verificationComment = comment;
        }
      })
      .addCase(verifyFixerDocument.rejected, (state, action) => {
        state.verifyingDocumentType = null;
        state.message = action.payload;
      });

    builder
      .addCase(updateFixerStatus.pending, (state) => {
        state.isUpdatingStatus = true;
        state.message = "";
      })
      .addCase(updateFixerStatus.fulfilled, (state, action) => {
        state.isUpdatingStatus = false;
        state.message = action.payload.message;
        if (state.fixer) state.fixer.status = action.payload.data?.status ?? state.fixer.status;
      })
      .addCase(updateFixerStatus.rejected, (state, action) => {
        state.isUpdatingStatus = false;
        state.message = action.payload;
      });
  },
});

export const { clearFixer } = slice.actions;
export default slice.reducer;
